import { useContext, useEffect, useRef, useState } from 'react';
import { PageTitleContext } from 'context/PageTitleContext';
import { CrumbsContext } from 'context/CrumbsContext';
import { ApiConsumer, Backend } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';
import { NotificationContext } from 'context/NotificationContext';
import { MonthlyCharts } from 'components/Charts/monthly-charts';
import {
  CogIcon,
  DocumentDownloadIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import { MusicChart } from 'components/Music/chart';
import ChartFilter from 'components/Reports/ChartFilter';

const fileDownload = require('js-file-download');

const PARTIAL_CONTENT_STATUS_CODE = 206;
const COMPLETE_SUCCESSFUL_RESPONSE = 200;

export default function PlaylistReport() {
  const [details, setDetails] = useState({
    chart_order: 'none',
    report: 'Playlist Report',
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  // Separate from results for the same reason as on the custom report page: the playlist table holds a
  // list and the charts hold the graphical object, and one state for both left stale data of the wrong
  // shape in whichever component was not refreshed.
  const [graphResults, setGraphResults] = useState({});
  const [page, setPage] = useState(1);
  let { setTitle } = useContext(PageTitleContext);
  let { setCrumbs } = useContext(CrumbsContext);
  const [graphicLoading, setGraphicLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  let { setNotification } = useContext(NotificationContext);
  const exportRef = useRef();
  

  const [downloadingReport, setDownloadingReport] = useState(false);

  useEffect(() => {
    setResults([]);
    setLoading(false);
  }, [details?.types]);

  function getMonthlyStats() {
    setGraphicLoading(true);
    ApiConsumer.get(API_ROUTES.BROADCAST.REPORTS.GRAPHICAL, {
      params: { ...details },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
    })
      .then((res) => {
        setGraphResults(res.data);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setGraphicLoading(false);
        setLoading(true);
      });
  }

  function searchReport() {
    setLoading(true);
    Backend.get(API_ROUTES.BROADCAST.REPORTS.PLAYLIST_QUERY, {
      params: { ...details, page: page },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
    })
      .then((res) => {
        setResults(res.data.data);
        setTotalCount(res.data.count);
        setTotalPages(res.data.pages);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }

  const changePage = (newP) => {
    setPage(newP, () => searchReport());
  };

  const qReportDownload = (reportName, reqTimeout, tryCount = 1) => {
    setTimeout(() => {
      Backend.get(API_ROUTES.BROADCAST.REPORTS.GET_EXISTING_FILE, {
        params: { reportName },
        paramSerializer: function (params) {
          return JSON.stringify(params);
        },
        responseType: 'blob',
      })
        .then((res) => {
          setNotification({
            show: true,
            title: 'Report Generation Complete',
            message: `The requested report will now download.`,
            time: 5000,
            type: 'success',
          });

          fileDownload(res.data, 'Playlist-Report.xlsx');
          setDownloadingReport(false);
        })
        .catch((e) => {
          console.log('report download error', e);
          if (e?.response?.status === 404 && tryCount < 4) {
            qReportDownload(reportName, reqTimeout, tryCount + 1);
          } else {
            setDownloadingReport(false);
            setNotification({
              show: true,
              title: 'Report Generation Failed',
              message: `The requested full report failed to generate. Please try again later`,
              time: 5000,
              type: 'error',
            });
          }
        });
    }, reqTimeout);
  };

  const downloadReport = () => {
    const config = {
      params: { ...details, page: page },
      paramSerializer: function (params) {
        return JSON.stringify(params);
      },
    };

    setDownloadingReport(true);


    Backend.get(API_ROUTES.BROADCAST.REPORTS.PLAYLIST_QUERY_DOWNLOAD, config)
      .then((res) => {
        qReportDownload(res.data.reportName, res.data.timeout);
        setNotification({
          show: true,
          title: 'Generating Report',
          message: `Downloading of full report should begin when it is ready.`,
          time: 15000,
        });
      })
      .catch((err) => {
        console.error(err);
        setDownloadingReport(false);
        setNotification({
          show: true,
          title: 'Generating Report Failed',
          message: `Please retry generating another report later`,
          time: 1500,
          type: 'error',
        });
      });
  };

  useEffect(() => {
    setTitle('Playlist Reports');
    setCrumbs([
      { name: 'Charts & Reports', link: '/reports/playlist' },
      {
        name: 'Playlist Reports',
        link: '/reports/playlist',
      },
    ]);
  }, []);


    useEffect(() => {
      if (details?.start_date && details?.end_date) {
        searchReport();
      }
    }, [page, details?.start_date, details?.end_date]);


  const renderTableHeader = () => (
    <>
      <h3 className="mb-2 font-semibold profile-overview-title mt-5 flex mx-full">
        {results.length ? (
          <span className="min-w-0 flex-1 items-center">
            Results ({totalCount})
          </span>
        ) : (
          <>
            <span className="min-w-0 flex-1 items-center">
              Search Playlists
            </span>{' '}
          </>
        )}
        <SearchIcon
          className="h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500"
          aria-hidden="true"
          onClick={() => {
            (details?.types !== 'graphical') ? searchReport() : getMonthlyStats()
          }}
        />
        {downloadingReport ? (
          <CogIcon className="animate-spin h-8 w-8 pt-1.5 cursor-pointer text-sabc-blue" />
        ) : (
          <DocumentDownloadIcon
            className="h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500"
            aria-hidden="true"
            onClick={downloadReport}
          />
        )}

        {/*<DocumentReportIcon className="h-8 w-8 pt-1.5 cursor-pointer hover:text-green-500" aria-hidden="true"/>*/}
      </h3>
    </>
  );
  return (
    <>
      <div className="h-full px-5 py-5 mt-0 mb-20">
       {
        <ChartFilter
          details={details}
          setDetails={setDetails}
          title={'List Limit'}
          limit={true}
          page={'playlist-reports'}
        />
        }
        {renderTableHeader()}
        {details?.types !== 'graphical' ? (
          <>
            <div ref={exportRef}>
              <MusicChart
                page={page}
                list={results}
                limit={details?.chart_type ?? 'full'}
                loading={loading}
                report={true}
                totalCount={totalCount}
                include_count={false}
                numPages={totalPages}
                setPage={changePage}
              />
            </div>
          </>
        ) : (
          <>
          <div ref={exportRef}>
            <MonthlyCharts results={graphResults} loading={graphicLoading} />
          </div>
          </>
        )}
        
      </div>
    </>
  );
}
